import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useApprovePasswordStore, useNotificationStore, usePostRequisitesStore } from '@lib/store';
import { RecipientTypeEnum } from '@shared/api/rest/models';
import type { GetRequisitesResponse } from '@shared/api/rest/payments';
import { ButtonV2, Constants, ErrorMessage, Input, Modal } from '@shared/widgets';
import { ValidationUtils } from '@shared/utils';

interface IForm {
  beneficiaryBankName: string;
  beneficiaryBankAddress: string;
  bic: string;
  iban: string;
  beneficiaryName: string;
}

const DEFAULT_VALUES = {
  beneficiaryBankName: '',
  beneficiaryBankAddress: '',
  bic: '',
  iban: '',
  beneficiaryName: '',
};

interface AddNewRecipientModalType {
  isOpen: boolean;
  typeRecipientChange: RecipientTypeEnum;
  selectedRecipient?: GetRequisitesResponse;
  onClose: () => void;
  setIsVisibleSelectAccountModal: (value: boolean) => void;
}

export const AddNewRecipientModal = (props: AddNewRecipientModalType) => {
  const postRequisitesStore = usePostRequisitesStore();
  const approvePasswordStore = useApprovePasswordStore();
  const notificationStore = useNotificationStore();

  const [isContinue, setIsContinue] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');

  const formControl = useForm<IForm>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });

  const onSubmit = async (data: IForm) => {
    try {
      await postRequisitesStore.post({
        BankName: data.beneficiaryBankName,
        BankAddress: data.beneficiaryBankAddress,
        BIC: data.bic,
        IBAN: data.iban,
        OwnerName: data.beneficiaryName,
      });
      setIsContinue(true);
    } catch (error) {
      setIsContinue(false);
      console.log(`Error: ${error}`);
    }
  };

  const confirmModal = async () => {
    await approvePasswordStore.approvePassword({ Password: password!, RequestId: postRequisitesStore.data?.Id });
    notificationStore.setSuccess(Constants.NEW_RECIPIENT_SUCCESS_MESSAGE);
    props.setIsVisibleSelectAccountModal(true);
    props.onClose();
    setPassword('');
    setIsContinue(false);
  };

  const handleBackRecipient = () => {
    setIsContinue(false);
    setPassword('');
  };

  const handleBackButtonClick = () => {
    props.setIsVisibleSelectAccountModal(true);
    props.onClose();
    formControl.reset(DEFAULT_VALUES);
  };

  const handleCloseModal = () => {
    formControl.reset(DEFAULT_VALUES);
    props.onClose();
  };

  useEffect(() => {
    if (props.isOpen) {
      if (props.typeRecipientChange === RecipientTypeEnum.Show && props.selectedRecipient) {
        formControl.reset({
          beneficiaryBankName: props.selectedRecipient.BankName,
          beneficiaryBankAddress: props.selectedRecipient.BankAddress,
          bic: props.selectedRecipient.BIC,
          iban: props.selectedRecipient.IBAN,
          beneficiaryName: props.selectedRecipient.OwnerName,
        });
      } else if (props.typeRecipientChange === RecipientTypeEnum.Add) {
        formControl.reset(DEFAULT_VALUES);
      }
    }
  }, [props.isOpen, props.typeRecipientChange, props.selectedRecipient, formControl.reset]);

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={handleCloseModal}
      title={props.typeRecipientChange === RecipientTypeEnum.Add ? 'New Recipient' : 'Recipient'}
    >
      {!isContinue ? (
        <form className='w-full' onSubmit={formControl.handleSubmit(onSubmit)}>
          <div>
            <div className='mb-[26px] w-full text-center text-base font-semibold text-[#666] uppercase'>Bank Account Details</div>
            <div className='mt-3'>
              <Controller
                name='beneficiaryBankName'
                control={formControl.control}
                rules={{
                  required: 'Please enter Beneficiary Bank Name',
                  maxLength: 100,
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>Beneficiary Bank Name</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter Beneficiary Bank Name'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      name='Beneficiary Bank'
                      required
                      error={error?.type}
                      readOnly={props.typeRecipientChange === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
              <Controller
                name='beneficiaryBankAddress'
                control={formControl.control}
                rules={{
                  required: ' Please enter Beneficiary Bank Address',
                  maxLength: 100,
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>Beneficiary Bank Address</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter Beneficiary Bank Address'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      error={error?.type}
                      name='Beneficiary Bank Address'
                      readOnly={props.typeRecipientChange === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
              <Controller
                name='bic'
                control={formControl.control}
                rules={{
                  required: 'Please enter BIC',
                  maxLength: 100,
                  pattern: { value: ValidationUtils.alphanumericRegex, message: 'The BIC field must be alphanumeric.' },
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>BIC</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter BIC'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      name='BIC'
                      error={error?.type}
                      readOnly={props.typeRecipientChange === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
              <Controller
                name='iban'
                control={formControl.control}
                rules={{
                  required: ' Please enter IBAN / Beneficiary Account',
                  maxLength: 100,
                  pattern: { value: ValidationUtils.alphanumericRegex, message: 'The IBAN field must be alphanumeric.' },
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>IBAN / Beneficiary Account</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter IBAN / Beneficiary Account'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      error={error?.type}
                      name='IBAN / Beneficiary Account'
                      readOnly={props.typeRecipientChange === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
              <Controller
                name='beneficiaryName'
                control={formControl.control}
                rules={{
                  required: 'Please enter Beneficiary Name',
                  maxLength: 100,
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>Beneficiary Name</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter Beneficiary Name'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      error={error?.type}
                      name='Beneficiary Name'
                      readOnly={props.typeRecipientChange === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
            </div>
          </div>
          <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
            <ButtonV2 fullWidth color='outlined' onClick={handleBackButtonClick}>
              Back
            </ButtonV2>
            {props.typeRecipientChange === RecipientTypeEnum.Add && (
              <ButtonV2 onClick={formControl.handleSubmit(onSubmit)} fullWidth color='primary'>
                Continue
              </ButtonV2>
            )}
          </div>
        </form>
      ) : (
        <div className='mt-3 w-full'>
          <div className='w-full'>
            <Input
              variant='outlined'
              rounded
              className='h-[48px]'
              placeholder='Password'
              value={password}
              onChange={setPassword}
              required
              type='password'
              name='Password'
            />
          </div>
          <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
            <ButtonV2 fullWidth color='outlined' onClick={handleBackRecipient}>
              Back
            </ButtonV2>
            <ButtonV2 fullWidth color='primary' disabled={password ? password.length < 1 : true} onClick={confirmModal}>
              Confirm
            </ButtonV2>
          </div>
        </div>
      )}
    </Modal>
  );
};
